// Copyright The OpenTelemetry Authors
// SPDX-License-Identifier: Apache-2.0

export interface StreamChatCallbacks {
  onTitle?: (title: string | null) => void;
  onStatus?: (label: string, tool?: string) => void;
  onDelta?: (text: string) => void;
  onDone?: (payload: { response: string; title: string | null; toolsUsed: string[] }) => void;
  onError?: (message: string, code?: string) => void;
}

interface SseEvent {
  event: string;
  data: Record<string, unknown>;
}

/**
 * Parse a chunk of buffered SSE text into complete events plus a remainder.
 * Events are separated by a blank line ("\n\n"); each event has `event:` and `data:` lines.
 */
const parseSseBuffer = (buffer: string): { events: SseEvent[]; rest: string } => {
  const events: SseEvent[] = [];
  const records = buffer.split('\n\n');
  const rest = records.pop() ?? '';

  for (const record of records) {
    if (!record.trim()) continue;
    let event = 'message';
    let dataLine = '';
    for (const line of record.split('\n')) {
      if (line.startsWith('event:')) {
        event = line.slice('event:'.length).trim();
      } else if (line.startsWith('data:')) {
        dataLine += line.slice('data:'.length).trim();
      }
    }
    try {
      events.push({ event, data: dataLine ? JSON.parse(dataLine) : {} });
    } catch {
      // Ignore malformed records.
    }
  }

  return { events, rest };
};

/**
 * POST a chat message to /api/chat requesting an SSE stream, dispatching parsed events to
 * the provided callbacks. Resolves when the stream completes.
 */
export const streamChat = async (
  url: string,
  body: object,
  callbacks: StreamChatCallbacks
): Promise<void> => {
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      accept: 'text/event-stream',
    },
    body: JSON.stringify(body),
  });

  if (!response.ok || !response.body) {
    throw new Error(`Chat stream request failed with status ${response.status}`);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  const dispatch = ({ event, data }: SseEvent) => {
    switch (event) {
      case 'title':
        callbacks.onTitle?.((data.title as string | null) ?? null);
        break;
      case 'status':
        callbacks.onStatus?.((data.label as string) ?? '', data.tool as string | undefined);
        break;
      case 'delta':
        if (data.text) callbacks.onDelta?.(data.text as string);
        break;
      case 'done':
        callbacks.onDone?.({
          response: (data.response as string) ?? '',
          title: (data.title as string | null) ?? null,
          toolsUsed: (data.tools_used as string[]) ?? [],
        });
        break;
      case 'error':
        callbacks.onError?.((data.message as string) ?? 'An error occurred', data.code as string | undefined);
        break;
      default:
        break;
    }
  };

  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const { events, rest } = parseSseBuffer(buffer);
    buffer = rest;
    events.forEach(dispatch);
  }

  // Flush any trailing complete event left in the buffer.
  const { events } = parseSseBuffer(buffer + '\n\n');
  events.forEach(dispatch);
};

export default streamChat;
